import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { ref, watch } from "vue";

export const breakpoints = useBreakpoints(breakpointsTailwind);
export const useMobile = breakpoints.smaller("lg");

// A colour token as hex, for libraries that take colour strings and shade them
// themselves (ApexCharts). CSS and inline styles use hsl(var(--name)) instead.
// Read from <body>: that is where the dark theme's `.dark` class sets them.
export function tokenColor(name: string) {
	const value = `hsl(${getComputedStyle(document.body).getPropertyValue(`--${name}`).trim()})`;
	const context = document.createElement("canvas").getContext("2d");
	if (!context) return value;
	context.fillStyle = value;
	return context.fillStyle;
}

function storedNumber(key: string, fallback: number, min: number, max: number) {
	if (typeof window === "undefined") return fallback;
	const stored = window.localStorage.getItem(key);
	if (stored === null) return fallback;
	const value = Number(stored);
	return Number.isFinite(value) && value >= min && value <= max ? value : fallback;
}

function persistNumber(key: string, value: number) {
	if (typeof window !== "undefined") window.localStorage.setItem(key, String(value));
}

export const rotateFactor = ref(storedNumber("belot.card-rotation", 6, 4, 10));
export const translateFactor = ref(storedNumber("belot.card-lift", 15, 12.5, 25));
// Added to the existing -17.5% overlap. The default makes card edges clearer
// without allowing an eight-card hand to escape its tray.
export const handSpacing = ref(storedNumber("belot.hand-spacing", 2.5, 0, 7.5));
watch(rotateFactor, (value) => persistNumber("belot.card-rotation", value));
watch(translateFactor, (value) => persistNumber("belot.card-lift", value));
watch(handSpacing, (value) => persistNumber("belot.hand-spacing", value));

export const touchCardPlayOptions = [
	{ value: "single", label: "Single tap", description: "Play the card immediately." },
	{ value: "double", label: "Double tap", description: "Tap the same card twice to play." },
	{ value: "confirm", label: "Confirm button", description: "Select a card, then tap Play." }
] as const;

export type TouchCardPlayMode = (typeof touchCardPlayOptions)[number]["value"];
const touchCardPlayStorageKey = "belot.touch-card-play-mode";

function storedTouchCardPlayMode(): TouchCardPlayMode {
	if (typeof window === "undefined") return "confirm";
	const stored = window.localStorage.getItem(touchCardPlayStorageKey);
	return touchCardPlayOptions.some(({ value }) => value === stored) ? (stored as TouchCardPlayMode) : "confirm";
}

// This is intentionally device-local: a player can use immediate play on a
// desktop and a confirmation flow on their phone with the same account.
export const touchCardPlayMode = ref<TouchCardPlayMode>(storedTouchCardPlayMode());
watch(touchCardPlayMode, (mode) => window.localStorage.setItem(touchCardPlayStorageKey, mode));

export function getGamemodeName(g: string) {
	switch (g) {
		case "C":
			return "Suit Clubs";
		case "D":
			return "Suit Diamonds";
		case "H":
			return "Suit Hearts";
		case "S":
			return "Suit Spades";
		case "N":
			return "No Trumps";
		case "A":
			return "All Trumps";
		default:
			return g;
	}
}

export function getDeclarationName(declaration: string) {
	switch (declaration) {
		case "P":
			return "Pass";
		default:
			return getGamemodeName(declaration);
	}
}

export const sizes = ["xs", "sm", "md", "lg", "xl"] as const;

export type Size = (typeof sizes)[number];

export function getSizeIndex(size: string) {
	return Math.max(sizes.indexOf(size as Size), 0); // default to 'md'
}
